import { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import type { CheckinFeedItem } from 'server/dbx/checkin-feedback';
import { CoachItemCard } from '@course/components/checkins/coach-item-card';
import type { CourseCoach } from 'server/dbx/checkin-coaches';
import {
  CheckinFeedback,
  isStuckStandup,
  needsReply,
  summaryAsksForNudge,
} from 'shared/checkin-feedback';

type Filter = 'need' | 'stuck' | 'summaries' | 'all';

type Props = {
  courseId: UUID;
  items: CheckinFeedItem[];
  viewerId: UUID;
  queueDays: number;
  replyEmailsEnabled: boolean;
  startDate?: string;
  studentHref(userId: UUID): string;
  /** The student's tracker counts this week, when the tracker is on and coach access hasn't ended. */
  trackerLine?(userId: UUID): string | undefined;
  /** Shown above the queue, e.g. who has sent this week's summary. */
  header?: ComponentChildren;
  /** The course's coaches when coaching is in use; empty hides My students. */
  coaches: CourseCoach[];
  onFeedback(itemId: UUID, feedback: CheckinFeedback): void;
};

function isStuckOrAsking(item: CheckinFeedItem) {
  return item.kind === 'standup' ? isStuckStandup(item.answers) : summaryAsksForNudge(item.body);
}

function FilterButton({
  isSelected,
  onClick,
  children,
}: {
  isSelected: boolean;
  onClick(): void;
  children: ComponentChildren;
}) {
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      class={`px-3 py-1 rounded-full border text-sm ${
        isSelected
          ? 'bg-indigo-50 border-indigo-500 text-indigo-800 font-semibold'
          : 'bg-white border-gray-300 text-gray-700'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function EmptyState({ title, children }: { title: string; children: ComponentChildren }) {
  return (
    <div class="bg-white rounded-sm border p-6 flex flex-col gap-2 text-sm">
      <strong class="text-gray-800">{title}</strong>
      <span class="text-gray-600">{children}</span>
    </div>
  );
}

export function CheckinRepliesView({
  courseId,
  items,
  viewerId,
  queueDays,
  replyEmailsEnabled,
  startDate,
  studentHref,
  trackerLine,
  header,
  coaches,
  onFeedback,
}: Props) {
  const [filter, setFilter] = useState<Filter>('need');
  // Each coach works their own queue: their students plus anyone with no
  // coach, so nobody falls through. Everyone is for covering a colleague.
  const isMine = (item: CheckinFeedItem) => !item.coachUserId || item.coachUserId === viewerId;
  const [scope, setScope] = useState<'mine' | 'all'>(() =>
    coaches.length && items.some((item) => item.coachUserId === viewerId) ? 'mine' : 'all',
  );
  const scoped = coaches.length && scope === 'mine' ? items.filter(isMine) : items;
  const coachName = (id?: UUID) => coaches.find((c) => c.id === id)?.name;
  // Items a coach acted on stay in view until the filter changes, so a card
  // doesn't vanish from under the coach the moment they react to it.
  const [handled, setHandled] = useState<Set<UUID>>(new Set());

  const matchers: Record<Filter, (item: CheckinFeedItem) => boolean> = {
    need: needsReply,
    stuck: (item) => isStuckOrAsking(item) && needsReply(item),
    summaries: (item) => item.kind === 'summary',
    all: () => true,
  };
  const count = (f: Filter) => scoped.filter(matchers[f]).length;
  const needCount = count('need');
  const elsewhere = items.filter(needsReply).length - needCount;
  const hasSummaries = items.some((item) => item.kind === 'summary');
  const shown = scoped.filter((item) => matchers[filter](item) || handled.has(item.id));
  const studentsIn = (list: CheckinFeedItem[]) => new Set(list.map((i) => i.userId)).size;

  function choose(next: Filter) {
    setFilter(next);
    setHandled(new Set());
  }

  return (
    <div class="flex flex-col gap-3">
      {header}
      {coaches.length > 0 && (
        <div class="flex flex-wrap items-center gap-2" role="group" aria-label="Whose students">
          <FilterButton isSelected={scope === 'mine'} onClick={() => setScope('mine')}>
            My students · {studentsIn(items.filter(isMine))}
          </FilterButton>
          <FilterButton isSelected={scope === 'all'} onClick={() => setScope('all')}>
            Everyone · {studentsIn(items)}
          </FilterButton>
          {scope === 'mine' && elsewhere > 0 && (
            <span class="text-sm text-gray-600">
              {elsewhere} more need a reply in other coaches’ queues
            </span>
          )}
        </div>
      )}
      <div class="flex flex-col gap-2">
        <strong class="text-gray-800">
          {needCount
            ? `${needCount} of ${scoped.length} need a reply`
            : `All ${scoped.length} have a reply or reaction`}
        </strong>
        <div class="flex flex-wrap items-center gap-2">
          <FilterButton isSelected={filter === 'need'} onClick={() => choose('need')}>
            Needs a reply · {needCount}
          </FilterButton>
          <FilterButton isSelected={filter === 'stuck'} onClick={() => choose('stuck')}>
            Stuck or asking · {count('stuck')}
          </FilterButton>
          {hasSummaries && (
            <FilterButton isSelected={filter === 'summaries'} onClick={() => choose('summaries')}>
              Weekly summaries · {count('summaries')}
            </FilterButton>
          )}
          <FilterButton isSelected={filter === 'all'} onClick={() => choose('all')}>
            All · {scoped.length}
          </FilterButton>
        </div>
        <span class="text-sm text-gray-600">
          Newest first. Covers the last {queueDays} days; older standups are on each student’s page.
        </span>
      </div>

      {shown.length === 0 && filter === 'need' && (
        <EmptyState title="You’re caught up.">
          New standups show up here as students post them.{' '}
          <button type="button" class="text-indigo-600 underline" onClick={() => choose('all')}>
            See all
          </button>
        </EmptyState>
      )}
      {shown.length === 0 && filter === 'stuck' && (
        <EmptyState title="No one stuck or asking for a nudge is waiting.">
          Standups with something under a stuck question, and summaries that ask for a nudge, show
          here until a coach reacts or replies.
        </EmptyState>
      )}
      {shown.length === 0 && filter === 'summaries' && (
        <EmptyState title="No weekly summaries yet.">
          Summaries show here as students send them.
        </EmptyState>
      )}
      {shown.length === 0 && filter === 'all' && (
        <EmptyState title={`No standups in the last ${queueDays} days.`}>
          Standups show here as students post them.
        </EmptyState>
      )}

      {shown.map((item) => (
        <CoachItemCard
          key={item.id}
          courseId={courseId}
          item={item}
          viewerId={viewerId}
          startDate={startDate}
          replyEmailsEnabled={replyEmailsEnabled}
          coachNote={
            !coaches.length || item.coachUserId === viewerId
              ? undefined
              : item.coachUserId
                ? `${coachName(item.coachUserId) || 'Another coach'}’s student`
                : 'No coach'
          }
          studentHref={studentHref(item.userId)}
          trackerLine={item.kind === 'standup' ? trackerLine?.(item.userId) : undefined}
          onFeedback={(feedback) => {
            setHandled((ids) => new Set(ids).add(item.id));
            onFeedback(item.id, feedback);
          }}
        />
      ))}
    </div>
  );
}
